/**
 * OE Snapshot service for Hugo_OESnapshots Firestore collection.
 *
 * Document structure:
 * - organizationId: string
 * - scope: map { type: string, id: string }
 * - period: string (e.g., '2026-Q1', '2026-03')
 * - calculatedAt: Timestamp
 * - oeScores: map { overall, reliability, efficiency, productivity, safety }
 * - elementScores: map { driftPerception, driftWork, traps, roadblocks, landmines } each with { R, E, P, S }
 * - warnings: array [{ quadrant, score, threshold, message }]
 * - responseCount: number
 * - perceptionScores: map { reliability, efficiency, productivity, safety } — surveys only (P2-35)
 * - conditionScores:  map { reliability, … } — the "Reality" contribution from approved OE Conditions
 * - conditionLoad:    { counted, active, resolvedDecaying, people, basis, cap }
 * - basis:            { responseCount, teamsCount, headcount, lowConfidence, text } (P2-38 rollup)
 *
 * Scoring (docs/beta/OE-SCORING.md): oeScores = perception (headcount-weighted rollup of survey
 * responses, `utils/rollups.js`) + capped condition load (`utils/conditionLoad.js`).
 */
import { db } from '../firebase';
import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  Timestamp,
} from 'firebase/firestore';
import {
  calculateResponseREPS,
  aggregateREPSScores,
  checkThresholds,
  filterResponsesByScope,
  aggregateElementScores,
} from '../assistants/drew/utils/scoring';
import { DEFAULT_THRESHOLDS } from '../assistants/drew/utils/constants';
import { normalizeThresholds } from '../assistants/drew/utils/bands';
import { computeConditionLoad, blendScores, normalizeScoring } from '../assistants/drew/utils/conditionLoad';
import { rollupScores, nodeForScope, headcountForScope } from '../assistants/drew/utils/rollups';

const COLLECTION_NAME = 'Hugo_OESnapshots';
const NOTIFICATIONS_COLLECTION = 'Hugo_Notifications';
const TRLS_COLLECTION = 'Hugo_TRLs';

// calculateElementScores is now imported from scoring.js as aggregateElementScores

/**
 * Get the current period string (YYYY-MM format)
 */
function getCurrentPeriod() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
}

/**
 * Pure snapshot math (exported for tests / scripts): perception rollup + condition load + warnings.
 * Perception = headcount-weighted rollup when `org` is given (P2-38), else the plain mean of the
 * scoped responses (the pre-Beta-1 behaviour). Warnings compare the BLENDED score with the threshold.
 */
export function computeSnapshotData({ organizationId, scope, responses, scopedResponses, thresholds, period, org = null, conditions = [], scoring = null, now = Date.now() }) {
  const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
  const scoped = scopedResponses || filterResponsesByScope(responses, scope);
  const tN = normalizeThresholds(thresholds);
  const cfg = normalizeScoring(scoring);

  // 1. Perception (surveys)
  let perception;
  let basis;
  const node = org ? nodeForScope(rollupScores(org, responses), scope) : null;
  if (node && node.scores) {
    perception = { ...node.scores };
    basis = { responseCount: node.responseCount, teamsCount: node.teamsCount, headcount: node.headcount, lowConfidence: node.lowConfidence, text: node.basis };
  } else {
    const raw = aggregateREPSScores(scoped.map((r) => ({ repsScores: r.repsScores || calculateResponseREPS(r.answers || {}) })));
    perception = { reliability: round2(raw.reliability), efficiency: round2(raw.efficiency), productivity: round2(raw.productivity), safety: round2(raw.safety) };
    basis = { responseCount: scoped.length, teamsCount: 0, headcount: 0, lowConfidence: scoped.length < 5, text: `based on ${scoped.length} ${scoped.length === 1 ? 'response' : 'responses'}` };
  }

  // 2. Reality (approved OE Conditions in scope, per 100 people)
  const headcount = org ? headcountForScope(org, scope) : 0;
  const load = computeConditionLoad({ conditions, scope, headcount, responseCount: scoped.length, scoring: cfg, now });
  const blend = blendScores(perception, load, cfg);

  const repsScores = blend.scores;
  const overall = round2((repsScores.reliability + repsScores.efficiency + repsScores.productivity + repsScores.safety) / 4);
  const oeScores = { overall, ...repsScores };
  const elementScores = aggregateElementScores(scoped);
  const warnings = checkThresholds(repsScores, tN);
  if (blend.mode === 'conditions') {
    // The score's basis is the conditions, not the responses (surveys stay in `basis.responseCount` for reference).
    const n = load.active + load.resolvedDecaying;
    const people = load.people > 0 ? ` · ${load.people} people` : '';
    basis = { ...basis, lowConfidence: false, text: n === 0 ? 'no approved OE Conditions yet' : `based on ${n} approved OE ${n === 1 ? 'Condition' : 'Conditions'}${people}` };
  }

  return {
    organizationId,
    scope,
    period: period || getCurrentPeriod(),
    calculatedAt: Timestamp.now(),
    oeScores,
    perceptionScores: blend.perception,
    conditionScores: blend.contribution,
    conditionLoad: { counted: load.counted, active: load.active, resolvedDecaying: load.resolvedDecaying, people: load.people, basis: load.basis, cap: blend.cap },
    scoringMode: blend.mode,
    basis,
    elementScores,
    warnings,
    responseCount: scoped.length,
  };
}

/**
 * Create an OE snapshot from responses, including threshold warning detection.
 * Warnings are stored in the snapshot and trigger notification creation.
 *
 * @param {Object} params
 * @param {string} params.organizationId - Organization ID
 * @param {Object} params.scope - { type: 'organization'|'site'|'department'|'team', id: string }
 * @param {Array} params.responses - Array of response objects with answers
 * @param {Object} params.thresholds - Per-quadrant thresholds (defaults to DEFAULT_THRESHOLDS)
 * @param {string} [params.period] - Period string (defaults to current YYYY-MM)
 * @param {Object} [params.org] - Organization doc (sites/departments/teams/membersIndex) → headcount-weighted rollup (P2-38)
 * @param {Array}  [params.conditions] - The org's OE Conditions (approved / resolution pending / resolved) → condition load (P2-35)
 * @param {Object} [params.scoring] - Organization scoring settings (weights, cap, decay); defaults in conditionLoad.js
 * @returns {Object} Created snapshot document
 */
export async function createOESnapshot({
  organizationId,
  scope = { type: 'organization', id: null },
  responses,
  thresholds = DEFAULT_THRESHOLDS,
  period,
  org = null,
  conditions = [],
  scoring = null,
}) {
  if (!organizationId) throw new Error('organizationId is required');
  const conditionsOnly = normalizeScoring(scoring).mode === 'conditions';
  // Conditions-only scoring (Tim 20 Oct): a snapshot needs no survey responses; perception is
  // reference data and may be empty. Blend mode keeps the original guards.
  if (!conditionsOnly && (!responses || responses.length === 0)) throw new Error('No responses to calculate from');

  // Filter responses by scope
  const scopedResponses = filterResponsesByScope(responses || [], scope);
  if (!conditionsOnly && scopedResponses.length === 0) throw new Error('No responses match the specified scope');

  const snapshotData = computeSnapshotData({ organizationId, scope, responses, scopedResponses, thresholds, period, org, conditions, scoring });

  // Store snapshot in Firestore
  const docRef = await addDoc(collection(db, COLLECTION_NAME), snapshotData);
  const createdSnapshot = { id: docRef.id, ...snapshotData };

  // If warnings were detected, create notifications
  if (snapshotData.warnings.length > 0) {
    await createWarningNotifications(organizationId, scope, snapshotData.warnings);
  }

  return createdSnapshot;
}

/**
 * Create notification documents for threshold warnings
 * @param {string} organizationId
 * @param {Object} scope
 * @param {Array} warnings - Array of warning objects
 */
async function createWarningNotifications(organizationId, scope, warnings) {
  try {
    for (const warning of warnings) {
      const notificationData = {
        organizationId,
        type: 'threshold_exceeded',
        scope,
        quadrant: warning.quadrant,
        score: warning.score,
        threshold: warning.threshold,
        message: warning.message,
        read: false,
        createdAt: Timestamp.now(),
      };

      await addDoc(collection(db, NOTIFICATIONS_COLLECTION), notificationData);
    }
  } catch (error) {
    // Log but don't throw - notifications are non-critical
    console.error('Failed to create warning notifications:', error);
  }
}

const RESPONSES_COLLECTION = 'Hugo_Responses';

/**
 * Refresh OE snapshots after a survey response is submitted, so dashboards
 * and warnings move in real time (fire-and-forget from the survey flow).
 *
 * Creates a fresh organization-level snapshot — plus a site-level one when the
 * respondent belongs to a site — from the survey's full response set, using
 * the organization's configured thresholds (falls back to defaults).
 */
export async function refreshSnapshotsForResponse({ organizationId, surveyId, respondentScope }) {
  // Basis: ALL of the organization's responses from the last 14 days — the
  // same window the comparison/rollup pages aggregate over. (Previously this
  // used only the submitted survey's responses, so a brand-new survey with a
  // single angry answer became the org's "latest" snapshot at 10.0 and
  // dragged the Barometer with it.)
  const cutoff = Timestamp.fromMillis(Date.now() - 14 * 24 * 3600 * 1000);
  const q = query(
    collection(db, RESPONSES_COLLECTION),
    where('organizationId', '==', organizationId),
    where('completedAt', '>=', cutoff)
  );
  const snap = await getDocs(q);
  const responses = snap.docs.map((d) => {
    const r = { id: d.id, ...d.data() };
    return {
      ...r,
      siteId: r.siteId ?? r.respondentScope?.siteId ?? null,
      departmentId: r.departmentId ?? r.respondentScope?.departmentId ?? null,
      teamId: r.teamId ?? r.respondentScope?.teamId ?? null,
    };
  });
  if (responses.length === 0) return;

  const inputs = await loadScoringInputs(organizationId);

  await createOESnapshot({ organizationId, scope: { type: 'organization', id: null }, responses, ...inputs });

  if (respondentScope?.siteId) {
    try {
      await createOESnapshot({ organizationId, scope: { type: 'site', id: respondentScope.siteId }, responses, ...inputs });
    } catch {
      /* a site with no matching responses is non-fatal */
    }
  }
}

/**
 * Everything a snapshot needs besides the responses: the org doc (thresholds, scoring settings,
 * structure + membersIndex for headcounts) and the OE Conditions that count toward the load.
 * Two reads (one doc + one query); safe to call from any client transition.
 */
export async function loadScoringInputs(organizationId) {
  let org = null;
  let thresholds = DEFAULT_THRESHOLDS;
  let scoring = null;
  try {
    const orgSnap = await getDoc(doc(db, 'Hugo_Organizations', organizationId));
    if (orgSnap.exists()) {
      org = { id: orgSnap.id, ...orgSnap.data() };
      if (org.thresholds) thresholds = org.thresholds;
      scoring = org.scoring || null;
    }
  } catch {
    /* fall back to defaults */
  }
  let conditions = [];
  try {
    conditions = await loadScoringConditions(organizationId);
  } catch (err) {
    console.warn('Condition load skipped (could not read OE Conditions):', err.message);
  }
  return { org, thresholds, scoring, conditions };
}

/** Approved / Resolution-pending / Resolved OE Conditions for an organization (the ones that can score). */
export async function loadScoringConditions(organizationId) {
  const q = query(
    collection(db, TRLS_COLLECTION),
    where('organizationId', '==', organizationId),
    where('status', 'in', ['approved', 'resolution_pending', 'resolved'])
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Recompute the org (and the condition's site) snapshot after an OE Condition changes state
 * (approved, resolved, cancelled, re-owned across scopes) so REPS cards move with reality (P2-35).
 * Fire-and-forget from trlService; a quiet org with no responses in the window does nothing.
 */
export async function refreshSnapshotsForCondition({ organizationId, siteId = null }) {
  if (!organizationId) return;
  const cutoff = Timestamp.fromMillis(Date.now() - 14 * 24 * 3600 * 1000);
  const q = query(
    collection(db, RESPONSES_COLLECTION),
    where('organizationId', '==', organizationId),
    where('completedAt', '>=', cutoff)
  );
  const snap = await getDocs(q);
  const responses = snap.docs.map((d) => {
    const r = { id: d.id, ...d.data() };
    return {
      ...r,
      siteId: r.siteId ?? r.respondentScope?.siteId ?? null,
      departmentId: r.departmentId ?? r.respondentScope?.departmentId ?? null,
      teamId: r.teamId ?? r.respondentScope?.teamId ?? null,
    };
  });
  const inputs = await loadScoringInputs(organizationId);
  const conditionsOnly = normalizeScoring(inputs.scoring).mode === 'conditions';
  if (responses.length === 0 && !conditionsOnly) return;
  await createOESnapshot({ organizationId, scope: { type: 'organization', id: null }, responses, ...inputs });
  if (siteId) {
    try {
      await createOESnapshot({ organizationId, scope: { type: 'site', id: siteId }, responses, ...inputs });
    } catch {
      /* no responses for that site in the window */
    }
  }
}

/**
 * Recompute the organization snapshot and one per site from today's inputs (approved OE
 * Conditions, scoring settings, the last 14 days of responses for perception). Admin action in
 * Settings → DREW → scoring; run it after changing the scoring mode or weights so stored numbers
 * match the formula. Returns { organization: true, sites: [{ id, ok, error? }] }.
 */
export async function recomputeOrganizationSnapshots(organizationId) {
  if (!organizationId) throw new Error('organizationId is required');
  const cutoff = Timestamp.fromMillis(Date.now() - 14 * 24 * 3600 * 1000);
  const q = query(collection(db, RESPONSES_COLLECTION), where('organizationId', '==', organizationId), where('completedAt', '>=', cutoff));
  const snap = await getDocs(q);
  const responses = snap.docs.map((d) => {
    const r = { id: d.id, ...d.data() };
    return { ...r, siteId: r.siteId ?? r.respondentScope?.siteId ?? null, departmentId: r.departmentId ?? r.respondentScope?.departmentId ?? null, teamId: r.teamId ?? r.respondentScope?.teamId ?? null };
  });
  const inputs = await loadScoringInputs(organizationId);
  await createOESnapshot({ organizationId, scope: { type: 'organization', id: null }, responses, ...inputs });
  const sites = [];
  for (const site of inputs.org?.sites || []) {
    try {
      await createOESnapshot({ organizationId, scope: { type: 'site', id: site.id }, responses, ...inputs });
      sites.push({ id: site.id, ok: true });
    } catch (err) {
      sites.push({ id: site.id, ok: false, error: err.message });
    }
  }
  return { organization: true, sites };
}

/**
 * Get all snapshots for an organization
 */
export async function getSnapshots(organizationId) {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('organizationId', '==', organizationId),
    orderBy('calculatedAt', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Get snapshots for a specific scope
 */
export async function getSnapshotsByScope(organizationId, scope) {
  const constraints = [
    where('organizationId', '==', organizationId),
    orderBy('calculatedAt', 'desc'),
  ];

  if (scope && scope.type && scope.type !== 'organization') {
    constraints.push(where('scope.type', '==', scope.type));
    constraints.push(where('scope.id', '==', scope.id));
  }

  const q = query(collection(db, COLLECTION_NAME), ...constraints);
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Subscribe to snapshots in real-time
 */
export function subscribeToSnapshots(organizationId, callback) {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('organizationId', '==', organizationId),
    orderBy('calculatedAt', 'desc')
  );
  return onSnapshot(q, (snapshot) => {
    const data = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
    callback(data);
  });
}

/**
 * Get the latest snapshot for an organization and scope
 */
export async function getLatestSnapshot(organizationId, scope) {
  const snapshots = await getSnapshotsByScope(organizationId, scope);
  return snapshots.length > 0 ? snapshots[0] : null;
}

export { COLLECTION_NAME, NOTIFICATIONS_COLLECTION };
